import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, X, Sparkles, Trophy, Star, ArrowRight, Loader2, Coins } from 'lucide-react';
import { useOutfitRating, OutfitRating } from '@/hooks/useOutfitRating';
import { useWeather } from '@/hooks/useWeather';
import { useStyleCredits, CREDIT_COSTS } from '@/hooks/useStyleCredits';
import { cn } from '@/lib/utils';

interface OOTDRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsufficientCredits?: () => void;
}

export const OOTDRatingModal: React.FC<OOTDRatingModalProps> = ({ 
  isOpen, 
  onClose,
  onInsufficientCredits 
}) => {
  const [step, setStep] = useState<'capture' | 'rating' | 'result'>('capture');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isRating, rating, rateOutfit, reset } = useOutfitRating();
  const { weather } = useWeather();
  const { balance } = useStyleCredits();

  const creditCost = CREDIT_COSTS.ootd_rating;
  const hasEnoughCredits = balance >= creditCost;

  const handleImageSelect = useCallback((file: File) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleSubmitForRating = async () => {
    if (!imageFile) return;
    
    // Check credits before proceeding
    if (!hasEnoughCredits) {
      onInsufficientCredits?.();
      return;
    }
    
    setStep('rating');
    const result = await rateOutfit(imageFile, { weather });
    
    // Handle insufficient credits from backend
    if (result?.insufficientCredits) {
      setStep('capture');
      onInsufficientCredits?.();
      return;
    }
    
    if (result?.rating) {
      setStep('result');
    } else {
      setStep('capture');
    }
  };

  const handleReset = () => {
    setStep('capture');
    setImageFile(null);
    setImagePreview(null);
    reset();
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const getScoreColor = (score: number) => {
    if (score >= 95) return 'text-aura-gold';
    if (score >= 90) return 'text-green-500';
    if (score >= 85) return 'text-blue-500';
    return 'text-muted-foreground';
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md mx-auto p-0 overflow-hidden bg-card border-border">
        <AnimatePresence mode="wait">
          {/* Capture Step */}
          {step === 'capture' && (
            <motion.div
              key="capture"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-display">Rate My OOTD</h2>
                  <p className="text-sm text-muted-foreground">Get AI feedback & earn Aura</p>
                </div>
                <button onClick={handleClose} className="p-2 rounded-full hover:bg-muted">
                  <X size={18} />
                </button>
              </div>

              {/* Credit Cost Badge */}
              <div className="flex justify-center mb-4">
                <div className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium",
                  hasEnoughCredits 
                    ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
                    : "bg-red-500/10 text-red-500 border border-red-500/20"
                )}>
                  <Coins size={12} />
                  <span>{creditCost} credits</span>
                  {!hasEnoughCredits && <span className="text-[10px]">(need more)</span>}
                </div>
              </div>

              {imagePreview ? (
                <div className="space-y-4">
                  <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-muted">
                    <img
                      src={imagePreview}
                      alt="Your outfit"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={handleReset}
                      className="absolute top-3 right-3 p-2 rounded-full bg-background/80 backdrop-blur hover:bg-background"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <Button
                    onClick={handleSubmitForRating}
                    disabled={!hasEnoughCredits}
                    className="w-full h-12 rounded-full bg-aura-gold text-background hover:bg-aura-gold/90 disabled:opacity-50"
                  >
                    <Sparkles size={18} className="mr-2" />
                    Rate This Look
                    <span className="ml-2 text-xs opacity-75">({creditCost} cr)</span>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-[3/4] rounded-2xl border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <div className="p-4 rounded-full bg-aura-gold/10 mb-4">
                      <Camera size={32} className="text-aura-gold" />
                    </div>
                    <p className="text-sm font-medium">Upload your OOTD</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Full-body photo works best
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              )}
            </motion.div>
          )}

          {/* Rating Step */}
          {step === 'rating' && (
            <motion.div
              key="rating"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-6 flex flex-col items-center justify-center min-h-[400px]"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="p-6 rounded-full bg-aura-gold/10 mb-6"
              >
                <Sparkles size={48} className="text-aura-gold" />
              </motion.div>
              <h3 className="text-xl font-display mb-2">Analyzing Your Style</h3>
              <p className="text-sm text-muted-foreground text-center">
                Evaluating silhouette, color harmony,<br />and overall aesthetic...
              </p>
              <div className="mt-6 flex items-center gap-2">
                <Loader2 size={16} className="animate-spin text-aura-gold" />
                <span className="text-xs text-muted-foreground">AURA Intelligence at work</span>
              </div>
            </motion.div>
          )}

          {/* Result Step */}
          {step === 'result' && rating && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-display">{rating.title}</h2>
                <button onClick={handleClose} className="p-2 rounded-full hover:bg-muted">
                  <X size={18} />
                </button>
              </div>

              {/* Score Circle */}
              <div className="flex items-center justify-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                  className={cn(
                    "w-28 h-28 rounded-full border-4 flex flex-col items-center justify-center",
                    rating.score >= 95 ? "border-aura-gold bg-aura-gold/10" :
                    rating.score >= 90 ? "border-green-500 bg-green-500/10" :
                    rating.score >= 85 ? "border-blue-500 bg-blue-500/10" :
                    "border-muted bg-muted/10"
                  )}
                >
                  <span className={cn("text-3xl font-bold", getScoreColor(rating.score))}>
                    {rating.score}
                  </span>
                  <span className="text-xs text-muted-foreground">/100</span>
                </motion.div>
              </div>

              {/* Vibe Check */}
              <div className="text-center mb-6">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aura-gold/10 border border-aura-gold/30"
                >
                  <Star size={14} className="text-aura-gold" />
                  <span className="text-sm font-medium text-aura-gold">{rating.vibeCheck}</span>
                </motion.div>
                <p className="text-sm text-muted-foreground mt-3">{rating.headline}</p>
              </div>

              {/* Strengths */}
              <div className="mb-5">
                <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  What's Working
                </h4>
                <div className="space-y-1.5">
                  {rating.strengths.map((strength, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + idx * 0.1 }}
                      className="flex items-center gap-2 text-sm"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      {strength}
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Elevation Tip */}
              <div className="p-3 rounded-xl bg-muted/50 border border-border mb-5">
                <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                  Elevation Tip
                </h4>
                <p className="text-sm">{rating.elevationTip}</p>
              </div>

              {/* Points Earned */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex items-center justify-between p-3 rounded-xl bg-aura-gold/10 border border-aura-gold/30"
              >
                <div className="flex items-center gap-2">
                  <Trophy size={18} className="text-aura-gold" />
                  <span className="text-sm font-medium">Aura Earned</span>
                </div>
                <span className="text-lg font-bold text-aura-gold">+{rating.auraPoints}</span>
              </motion.div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="flex-1 rounded-full"
                >
                  Rate Another
                </Button>
                <Button
                  onClick={handleClose}
                  className="flex-1 rounded-full bg-aura-gold text-background hover:bg-aura-gold/90"
                >
                  Done
                  <ArrowRight size={16} className="ml-1" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
